import {
  BoxGeometry,
  BufferGeometry,
  CylinderGeometry,
  Group,
  Matrix4,
  Mesh,
} from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";

import { STREET_NAME_PLAQUE_RENDER_CONFIG as CONFIG } from
  "../../config/streetNamePlaques.ts";
import { pointInPolygon } from "../../world/geometry.ts";
import type {
  RuntimeLinearFeature,
  RuntimeWorldTilePayloadV2,
} from "../../world/schema/runtimeTile.ts";
import type { TerrainTileData } from "../../world/schema/tile.ts";
import {
  isPedestrianStreetFeature,
  streetFeatureRoadEdgeWidthM,
  streetFeatureSurfaceElevationM,
} from "../../world/streets.ts";
import type { CityMaterialLibrary } from "../materials/cityMaterials.ts";

export interface StreetNamePlaquePlacement {
  readonly name: string;
  readonly roadId: string;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly yaw: number;
}

export interface StreetNamePlaqueBatch {
  readonly object: Group;
  readonly plaqueCount: number;
}

interface Endpoint {
  readonly feature: Readonly<RuntimeLinearFeature>;
  readonly point: Readonly<{ x: number; z: number }>;
  readonly outward: Readonly<{ x: number; z: number }>;
  readonly name: string;
}

export function streetNamePlaquePlacementsForTile(
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  terrain: Readonly<TerrainTileData> = tile.terrain,
): readonly Readonly<StreetNamePlaquePlacement>[] {
  const groups = new Map<string, Endpoint[]>();
  for (const feature of tile.transportLines) {
    const name = feature.name?.trim();
    if (
      feature.kind !== "road" ||
      name === undefined ||
      name.length === 0 ||
      isPedestrianStreetFeature(feature) ||
      feature.points.length < 2
    ) {
      continue;
    }
    const first = feature.points[0]!;
    const second = feature.points[1]!;
    const last = feature.points.at(-1)!;
    const beforeLast = feature.points.at(-2)!;
    for (const endpoint of [
      endpointRecord(feature, name, first, {
        x: second.x - first.x,
        z: second.z - first.z,
      }),
      endpointRecord(feature, name, last, {
        x: beforeLast.x - last.x,
        z: beforeLast.z - last.z,
      }),
    ]) {
      if (endpoint === null) continue;
      const key = Math.round(endpoint.point.x * 2) + ":" +
        Math.round(endpoint.point.z * 2);
      const values = groups.get(key);
      if (values === undefined) groups.set(key, [endpoint]);
      else values.push(endpoint);
    }
  }
  const placements: StreetNamePlaquePlacement[] = [];
  for (const endpoints of groups.values()) {
    const distinctNames = new Map<string, Endpoint>();
    for (const endpoint of endpoints) {
      const key = normalizedName(endpoint.name);
      if (!distinctNames.has(key)) distinctNames.set(key, endpoint);
    }
    if (distinctNames.size < 2) continue;
    for (const endpoint of [...distinctNames.values()].slice(0, 2)) {
      const point = clearPlaquePoint(endpoint, tile);
      if (point === null) continue;
      const y = streetFeatureSurfaceElevationM(
        endpoint.feature,
        terrain,
        tile.origin,
        point,
      );
      if (y === null) continue;
      placements.push({
        name: limitedName(endpoint.name),
        roadId: endpoint.feature.id,
        ...point,
        y: y + CONFIG.surfaceLiftM,
        yaw: Math.atan2(-endpoint.outward.z, endpoint.outward.x),
      });
    }
  }
  return placements;
}

function clearPlaquePoint(
  endpoint: Readonly<Endpoint>,
  tile: Readonly<RuntimeWorldTilePayloadV2>,
): Readonly<{ x: number; z: number }> | null {
  const right = { x: -endpoint.outward.z, z: endpoint.outward.x };
  const roadOffsetM = streetFeatureRoadEdgeWidthM(endpoint.feature) / 2 +
    CONFIG.roadsideClearanceM;
  const plaqueHalfWidthM = plaqueWidthM(endpoint.name) / 2;
  const primarySourceId = sourceFeatureId(endpoint.feature);
  const junctionClearanceM = tile.transportLines.reduce<number>(
    (largest, feature) =>
      feature.kind !== "road" ||
        sourceFeatureId(feature) === primarySourceId ||
        distanceToPolyline(endpoint.point, feature.points) > 0.75
        ? largest
        : Math.max(
          largest,
          streetFeatureRoadEdgeWidthM(feature) / 2 +
            plaqueHalfWidthM + CONFIG.otherRoadPlaqueClearanceM,
        ),
    Number(CONFIG.junctionAlongOffsetM),
  );
  for (let step = 0; step < CONFIG.fallbackAttemptCount; step += 1) {
    const alongM = junctionClearanceM +
      step * CONFIG.fallbackAlongStepM;
    for (const side of [1, -1] as const) {
      const point = {
        x: endpoint.point.x + endpoint.outward.x * alongM +
          right.x * roadOffsetM * side,
        z: endpoint.point.z + endpoint.outward.z * alongM +
          right.z * roadOffsetM * side,
      };
      if (
        point.x < 0.25 || point.z < 0.25 ||
        point.x >= tile.sizeM - 0.25 || point.z >= tile.sizeM - 0.25
      ) {
        continue;
      }
      if (tile.buildings.some((building) =>
        pointInPolygon(point, building.footprint)
      )) {
        continue;
      }
      if ((tile.vegetation ?? []).some((tree) =>
        Math.hypot(point.x - tree.position.x, point.z - tree.position.z) <
          CONFIG.treeClearanceM + Math.max(0.08, tree.trunkDiameterM ?? 0) / 2
      )) {
        continue;
      }
      const intersectsRoad = tile.transportLines.some((feature) =>
        feature.kind === "road" &&
        distanceToPolyline(point, feature.points) <
          streetFeatureRoadEdgeWidthM(feature) / 2 +
            (sourceFeatureId(feature) === primarySourceId
              ? CONFIG.otherRoadPlaqueClearanceM
              : plaqueHalfWidthM + CONFIG.otherRoadPlaqueClearanceM)
      );
      if (!intersectsRoad) return point;
    }
  }
  return null;
}

export function createStreetNamePlaqueBatch(
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  terrain: Readonly<TerrainTileData>,
  materials: CityMaterialLibrary,
): StreetNamePlaqueBatch | null {
  const placements = streetNamePlaquePlacementsForTile(tile, terrain);
  if (placements.length === 0) return null;
  const poleParts: BufferGeometry[] = [];
  const plateParts: BufferGeometry[] = [];
  const textParts: BufferGeometry[] = [];
  for (const placement of placements) {
    const widthM = plaqueWidthM(placement.name);
    const matrix = new Matrix4()
      .makeRotationY(placement.yaw)
      .setPosition(placement.x, placement.y, placement.z);
    const pole = new CylinderGeometry(
      CONFIG.poleRadiusM,
      CONFIG.poleRadiusM * 1.08,
      CONFIG.poleHeightM,
      8,
    );
    pole.translate(0, CONFIG.poleHeightM / 2, 0);
    pole.applyMatrix4(matrix);
    poleParts.push(pole);
    const plate = new BoxGeometry(
      widthM,
      CONFIG.plaqueHeightM,
      CONFIG.plaqueDepthM,
    );
    plate.translate(0, CONFIG.plaqueCenterHeightM, 0);
    plate.applyMatrix4(matrix);
    plateParts.push(plate);
    const text = bitmapTextGeometry(placement.name, widthM - 0.18);
    text.applyMatrix4(matrix);
    textParts.push(text);
  }
  const object = new Group();
  object.name = "runtime-street-name-plaques:" + tile.id;
  object.userData = {
    tileId: tile.id,
    sourceLayer: "official-grpk-road-names",
    sourceLicense: "CC-BY-4.0",
    placement: "named-road-junction-endpoints",
    representation: "vilnius-blue-double-faced-pixel-letter-plaques",
  };
  for (const [name, geometries, material] of [
    ["poles", poleParts, materials.streetPole],
    ["blue-plates", plateParts, materials.roadSignBlue],
    ["street-names", textParts, materials.roadSignIvory],
  ] as const) {
    const geometry = mergeOwned(geometries, "street-name-plaques:" + name);
    const mesh = new Mesh(geometry, material);
    mesh.name = object.name + ":" + name;
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    object.add(mesh);
  }
  return { object, plaqueCount: placements.length };
}

function plaqueWidthM(name: string): number {
  return Math.max(
    CONFIG.minimumPlaqueWidthM,
    Math.min(
      CONFIG.maximumPlaqueWidthM,
      name.length * CONFIG.characterWidthM + 0.28,
    ),
  );
}

function distanceToPolyline(
  point: Readonly<{ x: number; z: number }>,
  points: readonly Readonly<{ x: number; z: number }>[],
): number {
  let minimum = Number.POSITIVE_INFINITY;
  for (let index = 1; index < points.length; index += 1) {
    const from = points[index - 1]!;
    const to = points[index]!;
    const dx = to.x - from.x;
    const dz = to.z - from.z;
    const lengthSquared = dx * dx + dz * dz;
    const ratio = lengthSquared <= 0
      ? 0
      : Math.max(0, Math.min(1,
        ((point.x - from.x) * dx + (point.z - from.z) * dz) /
          lengthSquared,
      ));
    minimum = Math.min(minimum, Math.hypot(
      point.x - (from.x + dx * ratio),
      point.z - (from.z + dz * ratio),
    ));
  }
  return minimum;
}

function sourceFeatureId(feature: Readonly<RuntimeLinearFeature>): string {
  return feature.id.split("#", 1)[0]!;
}

function endpointRecord(
  feature: Readonly<RuntimeLinearFeature>,
  name: string,
  point: Readonly<{ x: number; z: number }>,
  delta: Readonly<{ x: number; z: number }>,
): Endpoint | null {
  const lengthM = Math.hypot(delta.x, delta.z);
  return lengthM < 0.5
    ? null
    : {
        feature,
        point,
        outward: { x: delta.x / lengthM, z: delta.z / lengthM },
        name,
      };
}

const GLYPHS: Readonly<Record<string, readonly string[]>> = Object.freeze({
  A: ["010", "101", "111", "101", "101"],
  B: ["110", "101", "110", "101", "110"],
  C: ["011", "100", "100", "100", "011"],
  D: ["110", "101", "101", "101", "110"],
  E: ["111", "100", "110", "100", "111"],
  F: ["111", "100", "110", "100", "100"],
  G: ["011", "100", "101", "101", "011"],
  H: ["101", "101", "111", "101", "101"],
  I: ["111", "010", "010", "010", "111"],
  J: ["001", "001", "001", "101", "010"],
  K: ["101", "101", "110", "101", "101"],
  L: ["100", "100", "100", "100", "111"],
  M: ["101", "111", "111", "101", "101"],
  N: ["101", "111", "111", "111", "101"],
  O: ["010", "101", "101", "101", "010"],
  P: ["110", "101", "110", "100", "100"],
  Q: ["010", "101", "101", "111", "011"],
  R: ["110", "101", "110", "101", "101"],
  S: ["011", "100", "010", "001", "110"],
  T: ["111", "010", "010", "010", "010"],
  U: ["101", "101", "101", "101", "111"],
  V: ["101", "101", "101", "101", "010"],
  W: ["101", "101", "111", "111", "101"],
  X: ["101", "101", "010", "101", "101"],
  Y: ["101", "101", "010", "010", "010"],
  Z: ["111", "001", "010", "100", "111"],
  "0": ["111", "101", "101", "101", "111"],
  "1": ["010", "110", "010", "010", "111"],
  "2": ["110", "001", "010", "100", "111"],
  "3": ["110", "001", "010", "001", "110"],
  "4": ["101", "101", "111", "001", "001"],
  "5": ["111", "100", "110", "001", "110"],
  "6": ["011", "100", "111", "101", "111"],
  "7": ["111", "001", "010", "010", "010"],
  "8": ["111", "101", "111", "101", "111"],
  "9": ["111", "101", "111", "001", "110"],
  ".": ["000", "000", "000", "000", "010"],
  " ": ["000", "000", "000", "000", "000"],
});

function bitmapTextGeometry(text: string, maximumWidthM: number): BufferGeometry {
  const normalized = asciiName(text);
  const columns = Math.max(1, normalized.length * 4 - 1);
  const pixel = Math.min(maximumWidthM / columns, 0.25 / 5);
  const widthM = columns * pixel;
  const parts: BufferGeometry[] = [];
  for (const [characterIndex, character] of [...normalized].entries()) {
    const rows = GLYPHS[character] ?? GLYPHS[" "]!;
    for (const [rowIndex, row] of rows.entries()) {
      for (let columnIndex = 0; columnIndex < row.length; columnIndex += 1) {
        if (row[columnIndex] !== "1") continue;
        for (const side of [-1, 1] as const) {
          const pixelGeometry = new BoxGeometry(pixel * 0.78, pixel * 0.78, 0.008);
          pixelGeometry.translate(
            -widthM / 2 +
              (characterIndex * 4 + columnIndex + 0.5) * pixel,
            CONFIG.plaqueCenterHeightM + 0.1 - (rowIndex + 0.5) * pixel,
            side * (CONFIG.plaqueDepthM / 2 + 0.007),
          );
          parts.push(pixelGeometry);
        }
      }
    }
  }
  return mergeOwned(parts, "street-name:text:" + normalized);
}

function limitedName(value: string): string {
  const normalized = value.trim().replace(/\s+/gu, " ");
  return normalized.length <= CONFIG.maximumLabelCharacters
    ? normalized
    : normalized.slice(0, CONFIG.maximumLabelCharacters - 2).trimEnd() + "..";
}

function asciiName(value: string): string {
  return value.toLocaleUpperCase("lt-LT").normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[^A-Z0-9. ]/gu, " ");
}

function normalizedName(value: string): string {
  return asciiName(value).replace(/\s+/gu, " ").trim();
}

function mergeOwned(
  geometries: readonly BufferGeometry[],
  name: string,
): BufferGeometry {
  const merged = mergeGeometries([...geometries]);
  for (const geometry of geometries) geometry.dispose();
  if (merged === null) throw new Error("Could not merge " + name);
  merged.name = name;
  merged.computeBoundingBox();
  merged.computeBoundingSphere();
  return merged;
}
